import { useState, useEffect } from "react";
import { forgotPasswordEmail, resetPassword } from "./services/api";

const ResetPasswordScreen = ({ navigateTo }) => {
  const [step, setStep] = useState("request"); // request | sent | reset | done
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // If opened from email link, skip to reset step
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    const e = params.get("email");
    if (t && e) {
      setToken(t);
      setEmail(e);
      setStep("reset");
    }
  }, []);

  const handleRequestReset = async () => {
    setError("");
    if (!email.trim()) {
      setError("Please enter your email");
      return;
    }
    setIsLoading(true);
    try {
      await forgotPasswordEmail(email);
      setStep("sent"); // always show sent regardless of whether email exists
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError("");
    if (!password || !confirmPassword) {
      setError("Please fill in both fields");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setIsLoading(true);
    try {
      const res = await resetPassword(email, token, password);
      if (!res.success) {
        setError(res.error || "Failed to reset password");
        return;
      }
      setStep("done");
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        {step === "request" && (
          <>
            <h2 className="applicant-theme-text">Reset Password</h2>
            <p className="text-sm text-gray-500 text-center mb-6">
              Enter your email and we'll send you a reset link.
            </p>
            {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={handleRequestReset}
              disabled={isLoading}
              className="buttons-indigo w-full"
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </button>
            <div className="buttons-container">
              <button
                onClick={() => navigateTo("login")}
                className="buttons-navigation"
              >
                Back to login
              </button>
            </div>
          </>
        )}

        {step === "sent" && (
          <>
            <h2 className="page-header text-center mb-2">Check your email</h2>
            <p className="text-sm text-gray-500 text-center mb-6">
              If an account exists for <strong>{email}</strong>, you'll receive
              a reset link shortly.
            </p>
            <button
              onClick={handleRequestReset}
              className="buttons-indigo w-full"
            >
              Resend Reset Link
            </button>
            <button
              onClick={() => navigateTo("login")}
              className="buttons-indigo w-full"
            >
              Back to login
            </button>
          </>
        )}

        {step === "reset" && (
          <>
            <h2 className="page-header text-center mb-2">New Password</h2>
            <p className="text-sm text-gray-500 text-center mb-6">
              Choose a new password for your account.
            </p>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <input
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={handleResetPassword}
              disabled={isLoading}
              className="buttons-indigo w-full"
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </button>
          </>
        )}

        {step === "done" && (
          <>
            <h2 className="page-header text-center mb-2">Password Reset</h2>
            <p className="text-sm text-gray-500 text-center mb-6">
              Your password has been updated. You can now log in.
            </p>
            <button
              onClick={() => navigateTo("login")}
              className="buttons-indigo w-full"
            >
              Go to login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordScreen;
